
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'

export const useRFIDTagStore = defineStore('rfidTag', () => {
  // State
  const tags = ref(new Map())
  const isLoading = ref(false)
  const error = ref(null)
  const pagination = ref({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  })

  // Computed
  const tagList = computed(() => Array.from(tags.value.values()))
  const tagCount = computed(() => tags.value.size)
  const tagsByDeviceType = computed(() => {
    const grouped = {}
    tagList.value.forEach(tag => {
      if (!grouped[tag.deviceType]) {
        grouped[tag.deviceType] = []
      }
      grouped[tag.deviceType].push(tag)
    })
    return grouped
  })

  // Actions
  async function fetchTags(deviceType = null, refresh = false) {
    if (isLoading.value && !refresh) return

    try {
      isLoading.value = true
      error.value = null

      const params = {
        limit: pagination.value.limit,
        offset: refresh ? 0 : pagination.value.offset,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      }
      if (deviceType) params.deviceType = deviceType

      const response = await axios.get('/api/rfid-tags', { params })
      const data = response.data

      if (data.success) {
        // If refreshing, clear existing tags
        if (refresh || pagination.value.offset === 0) {
          tags.value.clear()
        }

        // Add tags to the map
        data.data.tags.forEach(tag => {
          tags.value.set(tag._id, tag)
        })

        // Update pagination
        pagination.value = data.data.pagination
      } else {
        throw new Error(data.error || 'Failed to fetch tags')
      }
    } catch (err) {
      console.error('Error fetching RFID tags:', err)
      error.value = err.message
    } finally {
      isLoading.value = false
    }
  }

  async function fetchTagById(id) {
    try {
      const response = await axios.get(`/api/rfid-tags/${id}`)
      const data = response.data

      if (data.success) {
        tags.value.set(data.data._id, data.data)
        return data.data
      } else {
        throw new Error(data.error || 'Failed to fetch tag')
      }
    } catch (err) {
      console.error('Error fetching RFID tag:', err)
      throw err
    }
  }

  async function checkTagExists(tagId) {
    try {
      const response = await axios.get(`/api/rfid-tags/check/${encodeURIComponent(tagId)}`)
      const data = response.data
      return data.success ? data.data : { exists: false, tag: null }
    } catch (err) {
      console.error('Error checking RFID tag:', err)
      throw err
    }
  }

  async function createTag(tagData) {
    try {
      isLoading.value = true
      error.value = null

      const response = await axios.post('/api/rfid-tags', tagData)
      const data = response.data

      if (data.success) {
        tags.value.set(data.data._id, data.data)
        return data.data
      } else {
        throw new Error(data.error || 'Failed to create tag')
      }
    } catch (err) {
      console.error('Error creating RFID tag:', err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function updateTag(id, updates) {
    try {
      isLoading.value = true
      error.value = null

      const response = await axios.put(`/api/rfid-tags/${id}`, updates)
      const data = response.data

      if (data.success) {
        tags.value.set(data.data._id, data.data)
        return data.data
      } else {
        throw new Error(data.error || 'Failed to update tag')
      }
    } catch (err) {
      console.error('Error updating RFID tag:', err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function deleteTag(id) {
    try {
      isLoading.value = true
      error.value = null

      const response = await axios.delete(`/api/rfid-tags/${id}`)
      const data = response.data

      if (data.success) {
        tags.value.delete(id)
        return true
      } else {
        throw new Error(data.error || 'Failed to delete tag')
      }
    } catch (err) {
      console.error('Error deleting RFID tag:', err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function bulkDeleteTags(ids) {
    try {
      isLoading.value = true
      error.value = null

      const response = await axios.post('/api/rfid-tags/bulk/delete', { ids })
      const data = response.data

      if (data.success) {
        // Remove deleted tags from the map
        ids.forEach(id => tags.value.delete(id))
        return data.data.deletedCount
      } else {
        throw new Error(data.error || 'Failed to delete tags')
      }
    } catch (err) {
      console.error('Error bulk deleting RFID tags:', err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function markTagAsUsed(id) {
    try {
      const response = await axios.put(`/api/rfid-tags/${id}/use`)
      const data = response.data

      if (data.success) {
        tags.value.set(data.data._id, data.data)
        return data.data
      } else {
        throw new Error(data.error || 'Failed to mark tag as used')
      }
    } catch (err) {
      console.error('Error marking RFID tag as used:', err)
      throw err
    }
  }

  function clearError() {
    error.value = null
  }

  function clearStore() {
    tags.value.clear()
    error.value = null
    isLoading.value = false
    pagination.value = {
      total: 0,
      limit: 50,
      offset: 0,
      hasMore: false
    }
  }

  function getTagByTagId(tagId) {
    return tagList.value.find(tag => 
      tag.tagId.toLowerCase() === tagId.toLowerCase()
    )
  }

  function getTagsByDeviceType(deviceType) {
    return tagList.value.filter(tag => tag.deviceType === deviceType)
  }

  return {
    // State
    tags,
    isLoading,
    error,
    pagination,

    // Computed
    tagList,
    tagCount,
    tagsByDeviceType,

    // Actions
    fetchTags,
    fetchTagById,
    checkTagExists,
    createTag,
    updateTag,
    deleteTag,
    bulkDeleteTags,
    markTagAsUsed,
    clearError,
    clearStore,
    getTagByTagId,
    getTagsByDeviceType
  }
})